/**
 * Platform Data Configuration
 * Contains all platform information for the marketing platform pages
 * Colors are also stored in Supabase platforms table for consistency
 */

// Import logos (matching exact filenames in Assets/Images)
import GoogleLogo from '../../Assets/Images/Google_logo.png';
import YoutubeLogo from '../../Assets/Images/Youtube_logo.png';
import FacebookLogo from '../../Assets/Images/Facebook_logo.png';
import InstagramLogo from '../../Assets/Images/Instagram_logo.png';
import TiktokLogo from '../../Assets/Images/Tiktok_logo.png';
import XLogo from '../../Assets/Images/X_logo.png';
import LinkedinLogo from '../../Assets/Images/Linkedin_logo.png';
import PinterestLogo from '../../Assets/Images/Pinterest_logo.png';
import SnapchatLogo from '../../Assets/Images/Snapchat_logo.png';
import SpotifyLogo from '../../Assets/Images/Spotify_logo.png';
import AmazonLogo from '../../Assets/Images/Amazon_logo.png';
import ShopifyLogo from '../../Assets/Images/Shopify_logo.png';
import WixLogo from '../../Assets/Images/Wix_logo.png';
import WebflowLogo from '../../Assets/Images/Webflow_logo.png';
import WordpressLogo from '../../Assets/Images/Wordpress_logo.png';
import RedditLogo from '../../Assets/Images/Reddit_logo.png';
// Note: Telegram logo needs to be added to Assets/Images if not present
const TelegramLogo = null;

export const platforms = {
  google: {
    name: 'Google',
    slug: 'google',
    logo: GoogleLogo,
    accentColor: '#4285F4',
    tagline: 'Reach customers actively searching for your products and services.',
    description: 'Google is the world\'s most powerful search engine and advertising platform. With Google Ads, you can reach potential customers at the exact moment they\'re searching for what you offer, driving qualified traffic and conversions.',
    features: [
      { title: 'Google Ads (PPC)', description: 'Create targeted pay-per-click campaigns that appear when users search for relevant keywords.' },
      { title: 'Display Network', description: 'Reach audiences across millions of websites with visual banner and responsive ads.' },
      { title: 'Shopping Ads', description: 'Showcase your products directly in search results with images, prices, and reviews.' },
      { title: 'Performance Max', description: 'AI-powered campaigns that optimize across all Google properties automatically.' }
    ],
    benefits: [
      'Reach 8.5 billion daily searches',
      'Highly targeted advertising based on intent',
      'Measurable ROI with detailed analytics',
      'Flexible budget options for any business size',
      'Multiple ad formats and placements'
    ]
  },
  youtube: {
    name: 'Youtube',
    slug: 'youtube',
    logo: YoutubeLogo,
    accentColor: '#FF0000',
    tagline: 'Connect with billions through the power of video marketing.',
    description: 'YouTube is the world\'s largest video platform with over 2 billion monthly active users. It offers unprecedented opportunities for brands to connect through engaging video content, advertising, and influencer partnerships.',
    features: [
      { title: 'YouTube Ads', description: 'Reach your target audience through skippable, non-skippable, bumper, and discovery ad formats.' },
      { title: 'Content Strategy', description: 'Build a loyal subscriber base through consistent, valuable video content.' },
      { title: 'YouTube Shorts', description: 'Capture attention with short-form vertical videos reaching new audiences.' },
      { title: 'Analytics & Insights', description: 'Deep dive into audience behavior, watch time, and engagement metrics.' }
    ],
    benefits: [
      'Access to 2+ billion monthly users',
      'Second largest search engine after Google',
      'High engagement video format',
      'Powerful targeting and remarketing options',
      'Integration with Google Ads ecosystem'
    ]
  },
  facebook: {
    name: 'Facebook',
    slug: 'facebook',
    logo: FacebookLogo,
    accentColor: '#1877F2',
    tagline: 'Connect with your audience where they spend their time.',
    description: 'Facebook remains one of the most powerful advertising platforms with nearly 3 billion monthly active users. Its sophisticated targeting options and diverse ad formats make it essential for any digital marketing strategy.',
    features: [
      { title: 'Facebook Ads', description: 'Create compelling campaigns with image, video, carousel, and collection ads.' },
      { title: 'Audience Targeting', description: 'Reach specific demographics, interests, behaviors, and custom audiences.' },
      { title: 'Facebook Shops', description: 'Sell products directly on Facebook with integrated shopping features.' },
      { title: 'Messenger Marketing', description: 'Engage customers through personalized chatbot conversations.' }
    ],
    benefits: [
      'Nearly 3 billion monthly active users',
      'Highly sophisticated targeting options',
      'Cross-platform with Instagram and WhatsApp',
      'Diverse ad formats for any objective',
      'Powerful retargeting capabilities'
    ]
  },
  instagram: {
    name: 'Instagram',
    slug: 'instagram',
    logo: InstagramLogo,
    accentColor: '#E4405F',
    tagline: 'Inspire and engage through visual storytelling.',
    description: 'Instagram is the premier visual-first social platform perfect for brand storytelling, influencer marketing, and engaging with younger audiences through photos, videos, Stories, and Reels.',
    features: [
      { title: 'Instagram Ads', description: 'Run photo, video, carousel, and Stories ads to reach your ideal audience.' },
      { title: 'Instagram Reels', description: 'Create viral short-form video content to maximize organic reach.' },
      { title: 'Instagram Shopping', description: 'Tag products in posts and Stories for seamless shopping experiences.' },
      { title: 'Influencer Marketing', description: 'Partner with creators to authentically reach their engaged followers.' }
    ],
    benefits: [
      'Over 2 billion monthly active users',
      'Highly visual and engaging format',
      'Strong e-commerce integration',
      'Popular with Gen Z and Millennials',
      'Powerful Stories and Reels features'
    ]
  },
  tiktok: {
    name: 'TikTok',
    slug: 'tiktok',
    logo: TiktokLogo,
    accentColor: '#000000',
    tagline: 'Go viral with creative short-form video content.',
    description: 'TikTok is the fastest-growing social platform, perfect for reaching Gen Z and millennials through creative, authentic, and viral short-form video content.',
    features: [
      { title: 'TikTok Ads', description: 'Run in-feed ads, branded hashtag challenges, and TopView ads.' },
      { title: 'Creator Marketplace', description: 'Partner with TikTok creators for authentic influencer campaigns.' },
      { title: 'TikTok Shop', description: 'Enable in-app purchases with integrated e-commerce features.' },
      { title: 'Spark Ads', description: 'Boost organic content from your account or creators as ads.' }
    ],
    benefits: [
      'Over 1 billion monthly active users',
      'Highest engagement rates of any platform',
      'Viral content potential',
      'Authentic, creative format',
      'Rapidly growing e-commerce features'
    ]
  },
  x: {
    name: 'X',
    slug: 'x',
    logo: XLogo,
    accentColor: '#000000',
    tagline: 'Join the conversation and shape public discourse.',
    description: 'X (formerly Twitter) is the real-time conversation platform for news, trends, and direct customer engagement. It\'s ideal for brand awareness, customer service, and thought leadership.',
    features: [
      { title: 'X Ads', description: 'Promote tweets, accounts, and trends to reach your target audience.' },
      { title: 'X Spaces', description: 'Host live audio conversations to engage with your community.' },
      { title: 'X Premium', description: 'Leverage verified badges and enhanced features for brand credibility.' },
      { title: 'Real-time Engagement', description: 'Respond to trends and customer queries in real-time.' }
    ],
    benefits: [
      'Real-time news and conversation platform',
      'Direct access to journalists and influencers',
      'Excellent for customer service',
      'Hashtag campaigns for visibility',
      'Thought leadership opportunities'
    ]
  },
  linkedin: {
    name: 'LinkedIn',
    slug: 'linkedin',
    logo: LinkedinLogo,
    accentColor: '#0A66C2',
    tagline: 'Connect with professionals and grow your B2B presence.',
    description: 'LinkedIn is the world\'s largest professional networking platform, ideal for B2B marketing, thought leadership, recruitment, and targeting decision-makers in any industry.',
    features: [
      { title: 'LinkedIn Ads', description: 'Run sponsored content, message ads, and dynamic ads to reach professionals.' },
      { title: 'LinkedIn Pages', description: 'Build your company presence and share valuable industry content.' },
      { title: 'Lead Gen Forms', description: 'Capture leads directly within LinkedIn with pre-filled forms.' },
      { title: 'Thought Leadership', description: 'Establish authority through articles, newsletters, and expert posts.' }
    ],
    benefits: [
      'Over 900 million professional members',
      'Precise B2B targeting options',
      'High-quality lead generation',
      'Decision-maker access',
      'Industry thought leadership platform'
    ]
  },
  pinterest: {
    name: 'Pinterest',
    slug: 'pinterest',
    logo: PinterestLogo,
    accentColor: '#E60023',
    tagline: 'Inspire purchase decisions with visual discovery.',
    description: 'Pinterest is a visual discovery platform where users actively seek inspiration and plan purchases. It\'s perfect for lifestyle brands, e-commerce, and driving high-intent traffic.',
    features: [
      { title: 'Pinterest Ads', description: 'Create promoted pins, shopping ads, and video pins.' },
      { title: 'Shopping Features', description: 'Enable product catalogs and shoppable pins for seamless purchasing.' },
      { title: 'Idea Pins', description: 'Create multi-page video content for deeper storytelling.' },
      { title: 'Visual Search', description: 'Let users discover products through image-based searches.' }
    ],
    benefits: [
      'Users have high purchase intent',
      'Content has longer shelf life',
      'Strong e-commerce integration',
      'Visual discovery drives traffic',
      'Popular for home, fashion, and lifestyle'
    ]
  },
  snapchat: {
    name: 'Snapchat',
    slug: 'snapchat',
    logo: SnapchatLogo,
    accentColor: '#FFFC00',
    tagline: 'Engage Gen Z with interactive AR experiences.',
    description: 'Snapchat is an interactive multimedia platform perfect for reaching young audiences through AR filters, ephemeral content, and creative storytelling.',
    features: [
      { title: 'Snapchat Ads', description: 'Run single image, video, collection, and story ads.' },
      { title: 'AR Lenses', description: 'Create branded augmented reality experiences and filters.' },
      { title: 'Snap Map', description: 'Reach users based on real-time location data.' },
      { title: 'Spotlight', description: 'Leverage viral short-form video content opportunities.' }
    ],
    benefits: [
      'Reaches 75% of Gen Z and Millennials',
      'Unique AR and interactive features',
      'High engagement ephemeral content',
      'Strong brand awareness potential',
      'Location-based targeting'
    ]
  },
  spotify: {
    name: 'Spotify',
    slug: 'spotify',
    logo: SpotifyLogo,
    accentColor: '#1DB954',
    tagline: 'Reach listeners through the power of audio.',
    description: 'Spotify is the leading audio streaming platform for music and podcasts. It offers unique opportunities for audio advertising, podcast sponsorships, and reaching engaged listeners.',
    features: [
      { title: 'Audio Ads', description: 'Run audio ads between songs reaching millions of listeners.' },
      { title: 'Podcast Advertising', description: 'Sponsor popular podcasts or create branded content.' },
      { title: 'Video Ads', description: 'Display video ads during app browsing sessions.' },
      { title: 'Branded Playlists', description: 'Create sponsored playlists to associate your brand with music moments.' }
    ],
    benefits: [
      'Over 500 million monthly users',
      'Audio-first engagement without scrolling',
      'Mood and activity-based targeting',
      'Growing podcast advertising market',
      'Premium user demographics'
    ]
  },
  amazon: {
    name: 'Amazon',
    slug: 'amazon',
    logo: AmazonLogo,
    accentColor: '#FF9900',
    tagline: 'Sell where millions of customers are already shopping.',
    description: 'Amazon is the world\'s largest e-commerce marketplace, offering powerful advertising tools and access to millions of Prime shoppers ready to purchase.',
    features: [
      { title: 'Sponsored Products', description: 'Promote individual product listings in search results.' },
      { title: 'Sponsored Brands', description: 'Feature your brand logo and multiple products together.' },
      { title: 'Amazon DSP', description: 'Run programmatic display and video ads on and off Amazon.' },
      { title: 'Amazon Stores', description: 'Create a custom branded shopping experience.' }
    ],
    benefits: [
      'Access to 300+ million active customers',
      'High purchase intent shoppers',
      'Powerful advertising platform',
      'Prime member targeting',
      'Full-funnel marketing capabilities'
    ]
  },
  shopify: {
    name: 'Shopify',
    slug: 'shopify',
    logo: ShopifyLogo,
    accentColor: '#96BF48',
    tagline: 'Build and scale your e-commerce business.',
    description: 'Shopify is the leading e-commerce platform powering millions of online stores. It offers comprehensive tools for building, managing, and marketing your online business.',
    features: [
      { title: 'Store Setup', description: 'Build professional online stores with customizable themes.' },
      { title: 'Payment Processing', description: 'Accept payments from customers worldwide.' },
      { title: 'Marketing Tools', description: 'Email marketing, SEO, and social media integration.' },
      { title: 'Analytics', description: 'Track sales, customers, and marketing performance.' }
    ],
    benefits: [
      'Easy-to-use e-commerce platform',
      'Thousands of apps and integrations',
      'Multi-channel selling capabilities',
      'Built-in marketing features',
      'Scalable for any business size'
    ]
  },
  wix: {
    name: 'Wix',
    slug: 'wix',
    logo: WixLogo,
    accentColor: '#0C6EFC',
    tagline: 'Create stunning websites with drag-and-drop simplicity.',
    description: 'Wix is a user-friendly website builder offering drag-and-drop design tools, e-commerce solutions, and integrated marketing features for businesses of all sizes.',
    features: [
      { title: 'Website Builder', description: 'Create beautiful websites with intuitive drag-and-drop tools.' },
      { title: 'Wix Stores', description: 'Launch your online store with built-in e-commerce.' },
      { title: 'SEO Tools', description: 'Optimize your site for search engines with built-in tools.' },
      { title: 'Wix Marketing', description: 'Email campaigns, social posts, and analytics in one place.' }
    ],
    benefits: [
      'No coding required',
      'Professional templates for any industry',
      'All-in-one business solution',
      'Affordable pricing plans',
      'Built-in marketing tools'
    ]
  },
  webflow: {
    name: 'Webflow',
    slug: 'webflow',
    logo: WebflowLogo,
    accentColor: '#4353FF',
    tagline: 'Design and develop professional websites visually.',
    description: 'Webflow is a professional web design platform that combines visual design freedom with powerful CMS and e-commerce capabilities, perfect for designers and marketing teams.',
    features: [
      { title: 'Visual Design', description: 'Design pixel-perfect websites without writing code.' },
      { title: 'CMS', description: 'Create dynamic, content-driven websites with ease.' },
      { title: 'E-commerce', description: 'Build custom online stores with complete design control.' },
      { title: 'Interactions', description: 'Add animations and interactions without JavaScript.' }
    ],
    benefits: [
      'Complete design freedom',
      'Clean, production-ready code',
      'Powerful CMS for content teams',
      'Custom e-commerce experiences',
      'No developer dependency'
    ]
  },
  wordpress: {
    name: 'Wordpress',
    slug: 'wordpress',
    logo: WordpressLogo,
    accentColor: '#21759B',
    tagline: 'Power your digital presence with the web\'s most popular CMS.',
    description: 'WordPress powers over 40% of all websites on the internet. Its flexibility, vast plugin ecosystem, and SEO capabilities make it ideal for content marketing and business websites.',
    features: [
      { title: 'Content Management', description: 'Create and manage all types of content with an intuitive editor.' },
      { title: 'Plugin Ecosystem', description: 'Extend functionality with thousands of free and premium plugins.' },
      { title: 'SEO Optimization', description: 'Built-in SEO features and powerful plugins like Yoast.' },
      { title: 'E-commerce', description: 'WooCommerce powers millions of online stores.' }
    ],
    benefits: [
      'Powers 40%+ of all websites',
      'Highly customizable and flexible',
      'Massive plugin and theme ecosystem',
      'Excellent for SEO and content marketing',
      'Large developer community'
    ]
  },
  reddit: {
    name: 'Reddit',
    slug: 'reddit',
    logo: RedditLogo,
    accentColor: '#FF4500',
    tagline: 'Engage with passionate communities on any topic.',
    description: 'Reddit is a community-driven platform with passionate users organized into thousands of topic-specific subreddits. It\'s ideal for authentic engagement, market research, and niche targeting.',
    features: [
      { title: 'Reddit Ads', description: 'Run promoted posts, display ads, and video ads.' },
      { title: 'Community Targeting', description: 'Target specific subreddits and interest communities.' },
      { title: 'AMAs', description: 'Host Ask Me Anything sessions for authentic engagement.' },
      { title: 'Brand Presence', description: 'Participate authentically in relevant communities.' }
    ],
    benefits: [
      'Highly engaged, passionate communities',
      'Niche targeting by subreddit',
      'Authentic word-of-mouth potential',
      'Market research opportunities',
      'Early adopter audiences'
    ]
  },
  telegram: {
    name: 'Telegram',
    slug: 'telegram',
    logo: TelegramLogo,
    accentColor: '#0088cc',
    tagline: 'Build engaged communities with secure messaging.',
    description: 'Telegram is a secure messaging platform with powerful channel and group features. It\'s ideal for building communities, broadcasting content, and engaging with privacy-conscious audiences.',
    features: [
      { title: 'Channels', description: 'Broadcast content to unlimited subscribers with one-way messaging.' },
      { title: 'Groups', description: 'Create interactive communities with up to 200,000 members.' },
      { title: 'Bots', description: 'Automate engagement with custom chatbots and integrations.' },
      { title: 'Ads Platform', description: 'Reach users through sponsored messages in public channels.' }
    ],
    benefits: [
      'Over 700 million monthly users',
      'Privacy-focused audience',
      'Powerful bot and automation features',
      'High engagement in groups',
      'Growing advertising platform'
    ]
  }
};

// Get all platforms as an array
export const getAllPlatforms = () => Object.values(platforms);

// Get platform by slug
export const getPlatformBySlug = (slug) => platforms[slug] || null;

// Get platform slugs for routing
export const getPlatformSlugs = () => Object.keys(platforms);

export default platforms;
